/**
 * Administration › Conversations WhatsApp et SMS (/administration/conversations, docs/CONVERSATIONS.md § 8 et § 10) :
 * paramètres globaux du service, communs à toutes les filiales – droit « administrer » sur `conversations` (évalué pour
 * ALL par l'API, aussi depuis « Toutes mes filiales ») :
 *  - numéros PRODEVAL : canal, numéro E.164 (WhatsApp) ou expéditeur (SMS), identifiant AWS, nom affiché, usage
 *    (clients, support informatique), filiales servies et groupes de tri de chaque filiale (boîte « À trier ») ;
 *  - modèles de messages (déjà créés et approuvés chez Meta, dans la console) : code, canal, finalité, catégorie Meta,
 *    objet qui les propose, nom chez Meta, textes par langue avec leurs variables {{nom}}.
 * Ajout et modification dans une fenêtre (formulaires du socle : useFormulaire + schemaNumeroCanal / schemaModeleConversation
 * partagés avec l'API), jamais de suppression (désactivation par l'interrupteur « Actif »). Toute écriture est une action
 * sensible : reconnexion récente (useVerificationIdentite) ; succès par message éphémère, audit côté API.
 * API : GET/POST /v1/admin/conversations/numeros, PUT …/numeros/{id} ; GET/POST /v1/admin/conversations/modeles, PUT …/modeles/{id}.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CANAUX_CONVERSATION, CATEGORIES_META, FILIALES, FINALITES_CONSENTEMENT, LANGUES, USAGES_NUMERO, cleLibre, schemaModeleConversation,
  schemaNumeroCanal, variablesDuTexte,
  type CanalConversation, type FinaliteConsentement, type Langue, type UsageNumero,
} from '@agora/shared';
import { Button, DataTable, Dialog, SegmentedControl, SelectField, StatusBadge, TextArea, TextField, Toggle, cx } from '../../design';
import {
  ActionsFormulaire, ErreurApi, Formulaire, useApi, useConfirmation, useDroits, useFormats, useFormulaire, useMessageEphemere, useT,
  useVerificationIdentite, type EtatFormulaire,
} from '../../socle';
import {
  CLE_ADMIN_CONVERSATIONS, OBJETS_FIL, type ModeleEnregistre, type NumeroEnregistre,
} from '../conversations/commun';
import { SORTES_OBJETS_APPEL } from '../telephonie/sortes';
import { NonAutorise } from './PageNoeud';
import '../conversations/conversations.css';

const CLE_NUMEROS = [...CLE_ADMIN_CONVERSATIONS, 'numeros'] as const;
const CLE_MODELES = [...CLE_ADMIN_CONVERSATIONS, 'modeles'] as const;

/** Groupes de tri saisis séparés par des virgules, des points-virgules ou des espaces (noms des groupes en majuscules). */
const groupesDe = (texte: string | undefined) => (texte ?? '').split(/[\s,;]+/).map((g) => g.trim().toUpperCase()).filter(Boolean);

/** Fermeture d'une fenêtre (Annuler, Échap, ×) : immédiate si rien n'a changé, après confirmation sinon. */
function useFermeture(f: Pick<EtatFormulaire, 'modifie'>, fermer: () => void) {
  const { t } = useT();
  const confirmer = useConfirmation();
  return React.useCallback(async () => {
    if (!f.modifie || await confirmer({
      titre: t('conversations.abandon.titre'), texte: t('conversations.abandon.texte'),
      confirmer: t('conversations.abandon.confirmer'), annuler: t('conversations.abandon.continuer'), danger: true,
    })) fermer();
  }, [f.modifie, confirmer, fermer, t]);
}

// ------------------------------------------------------------------ numéros PRODEVAL
interface ValeursNumero {
  canal: CanalConversation; numero: string; expediteur: string; identifiantAws: string; nomAffiche: string; usage: UsageNumero;
  filiales: string[]; groupesTri: Record<string, string>; actif: boolean;
}

function FenetreNumero({ numero, onFermer }: { numero?: NumeroEnregistre; onFermer: () => void }) {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const annoncer = useMessageEphemere();
  const { avecVerification } = useVerificationIdentite();
  const f = useFormulaire({
    schema: schemaNumeroCanal,
    valeurs: {
      canal: numero?.canal ?? 'whatsapp', numero: numero?.numero ?? '', expediteur: numero?.expediteur ?? '',
      identifiantAws: numero?.identifiantAws ?? '', nomAffiche: numero?.nomAffiche ?? '', usage: numero?.usage ?? 'clients',
      filiales: numero?.filiales ?? [], groupesTri: Object.fromEntries(Object.entries(numero?.groupesTri ?? {}).map(([fil, g]) => [fil, g.join(', ')])),
      actif: numero?.actif ?? true,
    } satisfies ValeursNumero,
    // Corps de l'API (remplacement complet) : numéro sans espaces, expéditeur pour un SMS seulement, vide → null ; groupes
    // de tri des seules filiales servies.
    preparer: ({ numero: n, expediteur, groupesTri, ...v }) => ({
      ...v,
      numero: n.replace(/[\s.-]/g, '') || null,
      expediteur: v.canal === 'sms' ? expediteur.trim() || null : null,
      groupesTri: Object.fromEntries(v.filiales.map((fil) => [fil, groupesDe(groupesTri[fil])]).filter(([, g]) => g.length)),
    }),
    envoyer: (corps) => avecVerification(() => (numero
      ? api.put<NumeroEnregistre>(`/v1/admin/conversations/numeros/${encodeURIComponent(numero.id)}`, corps)
      : api.post<NumeroEnregistre>('/v1/admin/conversations/numeros', corps))),
    // Abandon de la vérification d'identité : undefined, la fenêtre reste ouverte avec la saisie.
    apresEnvoi: (n) => {
      if (!n) return;
      void qc.invalidateQueries({ queryKey: CLE_NUMEROS });
      annoncer({ statut: 'succes', titre: t('admin.conversations.numeros.enregistre', { nom: n.nomAffiche }) });
      onFermer();
    },
    messages: {
      numero: t('formulaires.erreurs.numeroE164'), expediteur: t('formulaires.erreurs.expediteurSms'),
      ...Object.fromEntries(FILIALES.map((x) => [`groupesTri.${x.code}`, t('formulaires.erreurs.groupeAgora')])),
    },
  });
  const fermer = useFermeture(f, onFermer);
  const canal = f.valeur('canal');
  const filiales = f.valeur('filiales');
  const erreurFiliales = f.erreurDe('filiales');
  const N = (c: 'canal' | 'numero' | 'expediteur' | 'identifiantAws' | 'nomAffiche' | 'usage' | 'filiales' | 'actif') => t(`admin.conversations.numeros.champs.${c}`);

  return (
    <Dialog title={numero ? t('admin.conversations.numeros.modifierTitre', { nom: numero.nomAffiche }) : t('admin.conversations.numeros.nouveau')}
      closeLabel={t('commun.fermer')} open size="lg" onOpenChange={(ouvert) => { if (!ouvert) void fermer(); }}>
      <Formulaire formulaire={f} className="agx-formulaire">
        <div className="agx-champs">
          <div className="ag-field">
            <label>{N('canal')}</label>
            <SegmentedControl ariaLabel={N('canal')} {...f.champ('canal')}
              options={CANAUX_CONVERSATION.map((c) => ({ value: c, label: t(`conversations.canaux.${c}`) }))} />
          </div>
          <div className="ag-field">
            <label>{N('usage')}</label>
            <SegmentedControl ariaLabel={N('usage')} {...f.champ('usage')}
              options={USAGES_NUMERO.map((u) => ({ value: u, label: t(`admin.conversations.numeros.usages.${u}`) }))} />
          </div>
          <TextField type="tel" autoComplete="off" label={N('numero')} {...f.champ('numero')} maxLength={20} required={canal === 'whatsapp'}
            help={t('admin.conversations.numeros.aide.numero')} />
          {canal === 'sms' ? (
            <TextField autoComplete="off" label={N('expediteur')} {...f.champ('expediteur')} maxLength={11}
              help={t('admin.conversations.numeros.aide.expediteur')} />
          ) : null}
          <TextField autoComplete="off" label={N('identifiantAws')} {...f.champ('identifiantAws')} maxLength={300} required
            help={t('admin.conversations.numeros.aide.identifiantAws')} />
          <TextField autoComplete="off" label={N('nomAffiche')} {...f.champ('nomAffiche')} maxLength={100} required autoFocus={!!numero} />
          <fieldset className={cx('ag-field agx-champ-large agx-conv-groupe', erreurFiliales && 'ag-field-error')}>
            <legend className="agx-conv-legende">{N('filiales')}</legend>
            <div className="agx-conv-filiales">
              {FILIALES.map((x) => <Toggle key={x.code} label={`${x.code} · ${x.ville}`} {...f.choix('filiales', x.code, FILIALES.map((y) => y.code))} />)}
            </div>
            {erreurFiliales ? <span className="ag-field-help">{erreurFiliales}</span> : null}
          </fieldset>
          {filiales.map((fil) => {
            const c = f.champ(`groupesTri.${fil}`);
            return (
              <TextField key={fil} autoComplete="off" label={t('admin.conversations.numeros.champs.groupesTri', { filiale: fil })} {...c} value={c.value ?? ''}
                maxLength={1000} help={t('admin.conversations.numeros.aide.groupesTri')} />
            );
          })}
          {numero ? <Toggle label={N('actif')} help={t('admin.conversations.aideActif')} {...f.interrupteur('actif')} /> : null}
        </div>
        <ActionsFormulaire formulaire={f} onAnnuler={() => void fermer()} />
      </Formulaire>
    </Dialog>
  );
}

// ------------------------------------------------------------------ modèles de messages
type CategorieMeta = (typeof CATEGORIES_META)[number];
interface ValeursModele {
  code: string; canal: CanalConversation; finalite: FinaliteConsentement; categorieMeta: CategorieMeta | ''; noeud: string;
  textes: Record<Langue, string>; nomMeta: string; actif: boolean;
}

function FenetreModele({ modele, onFermer }: { modele?: ModeleEnregistre; onFermer: () => void }) {
  const { t, lib } = useT();
  const fmt = useFormats();
  const api = useApi();
  const qc = useQueryClient();
  const annoncer = useMessageEphemere();
  const { avecVerification } = useVerificationIdentite();
  const f = useFormulaire({
    schema: schemaModeleConversation,
    valeurs: {
      code: modele?.code ?? '', canal: modele?.canal ?? 'whatsapp', finalite: modele?.finalite ?? 'service',
      categorieMeta: modele?.categorieMeta ?? 'utility', noeud: modele?.noeud ?? '',
      textes: Object.fromEntries(LANGUES.map((l) => [l, modele?.textes[l] ?? ''])) as Record<Langue, string>,
      nomMeta: modele?.nomMeta ?? '', actif: modele?.actif ?? true,
    } satisfies ValeursModele,
    // Corps de l'API : catégorie et nom chez Meta pour WhatsApp seulement, vide → null ; textes des langues renseignées.
    preparer: ({ categorieMeta, nomMeta, textes, ...v }) => ({
      ...v,
      categorieMeta: v.canal === 'whatsapp' ? categorieMeta || null : null,
      nomMeta: v.canal === 'whatsapp' ? nomMeta.trim() || null : null,
      textes: Object.fromEntries(Object.entries(textes).filter(([, x]) => x.trim()).map(([l, x]) => [l, x.trim()])),
    }),
    envoyer: (corps) => avecVerification(() => (modele
      ? api.put<ModeleEnregistre>(`/v1/admin/conversations/modeles/${encodeURIComponent(modele.id)}`, corps)
      : api.post<ModeleEnregistre>('/v1/admin/conversations/modeles', corps))),
    apresEnvoi: (m) => {
      if (!m) return;
      void qc.invalidateQueries({ queryKey: CLE_MODELES });
      annoncer({ statut: 'succes', titre: t('admin.conversations.modeles.enregistre', { code: m.code }) });
      onFermer();
    },
    messages: {
      code: t('formulaires.erreurs.codeModele'), nomMeta: t('formulaires.erreurs.nomMeta'), textes: t('formulaires.erreurs.textesModele'),
      finalite: t('formulaires.erreurs.smsMarketing'), categorieMeta: t('formulaires.erreurs.categorieMeta'),
    },
  });
  const fermer = useFermeture(f, onFermer);
  const canal = f.valeur('canal');
  const erreurTextes = f.erreurDe('textes');
  const variables = [...new Set(LANGUES.flatMap((l) => variablesDuTexte(f.valeur(`textes.${l}`) ?? '')))];
  const M = (c: 'code' | 'canal' | 'finalite' | 'categorieMeta' | 'noeud' | 'nomMeta' | 'textes' | 'actif') => t(`admin.conversations.modeles.champs.${c}`);
  const noeuds: string[] = [...OBJETS_FIL, ...(modele && !(OBJETS_FIL as readonly string[]).includes(modele.noeud) ? [modele.noeud] : [])];

  return (
    <Dialog title={modele ? t('admin.conversations.modeles.modifierTitre', { code: modele.code }) : t('admin.conversations.modeles.nouveau')}
      closeLabel={t('commun.fermer')} open size="lg" onOpenChange={(ouvert) => { if (!ouvert) void fermer(); }}>
      <Formulaire formulaire={f} className="agx-formulaire">
        <div className="agx-champs">
          <TextField autoComplete="off" label={M('code')} {...f.champ('code')} onChange={(v) => f.modifier('code', v.toLowerCase())} maxLength={100} required
            help={t('admin.conversations.modeles.aide.code')} autoFocus={!modele} />
          <SelectField label={M('noeud')} videLabel={t('admin.conversations.modeles.choisirObjet')} required {...f.champ('noeud')}
            options={noeuds.map((n) => ({ value: n, label: n in SORTES_OBJETS_APPEL ? t(`service.telephonie.affectation.ressources.${SORTES_OBJETS_APPEL[n as keyof typeof SORTES_OBJETS_APPEL].cle}`) : lib(n) }))} />
          <div className="ag-field">
            <label>{M('canal')}</label>
            <SegmentedControl ariaLabel={M('canal')} {...f.champ('canal')}
              options={CANAUX_CONVERSATION.map((c) => ({ value: c, label: t(`conversations.canaux.${c}`) }))} />
          </div>
          <div className="ag-field">
            <label>{M('finalite')}</label>
            <SegmentedControl ariaLabel={M('finalite')} {...f.champ('finalite')}
              options={FINALITES_CONSENTEMENT.map((x) => ({ value: x, label: t(`admin.conversations.modeles.finalites.${x}`) }))} />
            {f.champ('finalite').error ? <span className="ag-field-help">{f.champ('finalite').error}</span> : null}
          </div>
          {canal === 'whatsapp' ? (
            <>
              <SelectField label={M('categorieMeta')} videLabel={t('admin.conversations.modeles.choisirCategorie')} {...f.champ('categorieMeta')}
                options={CATEGORIES_META.map((c) => ({ value: c, label: t(`admin.conversations.modeles.categories.${c}`) }))} />
              <TextField autoComplete="off" label={M('nomMeta')} {...f.champ('nomMeta')} maxLength={512} required
                help={t('admin.conversations.modeles.aide.nomMeta')} />
            </>
          ) : null}
          <fieldset className={cx('ag-field agx-champ-large agx-conv-groupe', erreurTextes && 'ag-field-error')}>
            <legend className="agx-conv-legende">{M('textes')}</legend>
            <p className="agx-aide">{t('admin.conversations.modeles.aide.textes', { exemple: '{{reference}}' })}</p>
            <div className="agx-champs">
              {LANGUES.map((l) => (
                <TextArea key={l} label={t(cleLibre('langues', l))} {...f.champ(`textes.${l}`)} rows={3} maxLength={1024}
                  counterLabel={(n, max) => t('conversations.redaction.compteur', { n: fmt.nombre(n), max: fmt.nombre(max) })} />
              ))}
            </div>
            <span className="ag-field-help">
              {erreurTextes ?? (variables.length ? t('admin.conversations.modeles.variablesTrouvees', { variables: variables.join(', ') }) : t('admin.conversations.modeles.sansVariable'))}
            </span>
          </fieldset>
          {modele ? <Toggle label={M('actif')} help={t('admin.conversations.aideActif')} {...f.interrupteur('actif')} /> : null}
        </div>
        <ActionsFormulaire formulaire={f} onAnnuler={() => void fermer()} />
      </Formulaire>
    </Dialog>
  );
}

// ------------------------------------------------------------------ écran
export function AdministrationConversations() {
  const { t, lib } = useT();
  const idNumeros = React.useId();
  const idModeles = React.useId();
  const api = useApi();
  const droits = useDroits('conversations');
  const [numero, setNumero] = React.useState<{ numero?: NumeroEnregistre } | null>(null);
  const [modele, setModele] = React.useState<{ modele?: ModeleEnregistre } | null>(null);
  const numeros = useQuery({
    queryKey: CLE_NUMEROS, enabled: droits.administrer,
    queryFn: ({ signal }) => api.get<{ elements: NumeroEnregistre[] }>('/v1/admin/conversations/numeros', undefined, signal),
  });
  const modeles = useQuery({
    queryKey: CLE_MODELES, enabled: droits.administrer,
    queryFn: ({ signal }) => api.get<{ elements: ModeleEnregistre[] }>('/v1/admin/conversations/modeles', undefined, signal),
  });
  if (!droits.administrer) return <NonAutorise titre={t('admin.conversations.titre')} />;

  const erreur = (e: unknown) => (e ? t(cleLibre('erreurs', e instanceof ErreurApi ? e.code : 'interne')) : null);
  const etat = (actif: boolean) => <StatusBadge status={actif ? 'success' : 'neutral'} label={t(actif ? 'admin.conversations.etats.actif' : 'admin.conversations.etats.inactif')} />;
  const objet = (n: string) => (n in SORTES_OBJETS_APPEL ? t(`service.telephonie.affectation.ressources.${SORTES_OBJETS_APPEL[n as keyof typeof SORTES_OBJETS_APPEL].cle}`) : lib(n));
  const CN = (c: 'nomAffiche' | 'canal' | 'numero' | 'usage' | 'filiales' | 'etat' | 'actions') => t(`admin.conversations.numeros.colonnes.${c}`);
  const CM = (c: 'code' | 'canal' | 'objet' | 'finalite' | 'variables' | 'etat' | 'actions') => t(`admin.conversations.modeles.colonnes.${c}`);

  return (
    <>
      <div className="ag-pagehead">
        <div>
          <h1>{t('admin.conversations.titre')}</h1>
          <p className="agx-sous-titre">{t('admin.conversations.sousTitre')}</p>
        </div>
      </div>
      {numero ? <FenetreNumero key={numero.numero?.id ?? 'nouveau'} numero={numero.numero} onFermer={() => setNumero(null)} /> : null}
      {modele ? <FenetreModele key={modele.modele?.id ?? 'nouveau'} modele={modele.modele} onFermer={() => setModele(null)} /> : null}

      <section className="ag-glass-card agx-carte" aria-labelledby={idNumeros}>
        <div className="agx-appel-tete">
          <h2 id={idNumeros} className="agx-titre-carte">{t('admin.conversations.numeros.titre')}</h2>
          <Button variant="primary" icon="plus" onClick={() => setNumero({})}>{t('admin.conversations.numeros.ajouter')}</Button>
        </div>
        <DataTable
          dense ariaLabel={t('admin.conversations.numeros.titre')}
          columns={[
            { key: 'nomAffiche', label: CN('nomAffiche') },
            { key: 'canal', label: CN('canal'), width: 110 },
            { key: 'numero', label: CN('numero'), mono: true },
            { key: 'usage', label: CN('usage') },
            { key: 'filiales', label: CN('filiales'), mono: true },
            { key: 'etat', label: CN('etat'), width: 110 },
            { key: 'actions', label: CN('actions'), align: 'end', width: 140 },
          ]}
          rows={(numeros.data?.elements ?? []).map((n) => ({
            id: n.id,
            nomAffiche: n.nomAffiche,
            canal: t(`conversations.canaux.${n.canal}`),
            numero: n.numero ?? n.expediteur ?? '—',
            usage: t(`admin.conversations.numeros.usages.${n.usage}`),
            filiales: n.filiales.join(', '),
            etat: etat(n.actif),
            actions: (
              <Button size="sm" variant="quiet" icon="edit" aria-label={t('admin.conversations.numeros.modifierElement', { nom: n.nomAffiche })}
                onClick={() => setNumero({ numero: n })}>
                {t('admin.conversations.modifier')}
              </Button>
            ),
          }))}
          loading={numeros.isLoading} loadingLabel={t('app.chargement')}
          emptyLabel={erreur(numeros.error) ?? t('admin.conversations.numeros.vide')}
        />
      </section>

      <section className="ag-glass-card agx-carte" aria-labelledby={idModeles}>
        <div className="agx-appel-tete">
          <h2 id={idModeles} className="agx-titre-carte">{t('admin.conversations.modeles.titre')}</h2>
          <Button variant="primary" icon="plus" onClick={() => setModele({})}>{t('admin.conversations.modeles.ajouter')}</Button>
        </div>
        <p className="agx-aide">{t('admin.conversations.modeles.aide.liste')}</p>
        <DataTable
          dense ariaLabel={t('admin.conversations.modeles.titre')}
          columns={[
            { key: 'code', label: CM('code'), mono: true },
            { key: 'canal', label: CM('canal'), width: 110 },
            { key: 'objet', label: CM('objet') },
            { key: 'finalite', label: CM('finalite') },
            { key: 'variables', label: CM('variables'), mono: true },
            { key: 'etat', label: CM('etat'), width: 110 },
            { key: 'actions', label: CM('actions'), align: 'end', width: 140 },
          ]}
          rows={(modeles.data?.elements ?? []).map((m) => ({
            id: m.id,
            code: m.code,
            canal: t(`conversations.canaux.${m.canal}`),
            objet: objet(m.noeud),
            finalite: t(`admin.conversations.modeles.finalites.${m.finalite}`),
            variables: m.variables.join(', ') || '—',
            etat: etat(m.actif),
            actions: (
              <Button size="sm" variant="quiet" icon="edit" aria-label={t('admin.conversations.modeles.modifierElement', { code: m.code })}
                onClick={() => setModele({ modele: m })}>
                {t('admin.conversations.modifier')}
              </Button>
            ),
          }))}
          loading={modeles.isLoading} loadingLabel={t('app.chargement')}
          emptyLabel={erreur(modeles.error) ?? t('admin.conversations.modeles.vide')}
        />
      </section>
    </>
  );
}
